import { useEffect, useState, type ReactNode } from 'react';
import { AutoComplete, Segmented, Tabs, Tooltip } from 'antd';
import type { TableProps } from 'antd';
import {
  CheckCircleOutlined,
  DeleteOutlined,
  ExclamationCircleOutlined,
  PlayCircleOutlined,
  PlusOutlined,
  QuestionCircleOutlined,
  ReloadOutlined,
  SaveOutlined,
  WarningOutlined,
} from '@ant-design/icons';

import { DataTable } from '../../../../components/shared';
import { Badge, Button, Checkbox, Input, Select, Textarea } from '../../../../components/ui';
import { useT } from '../../../../i18n';
import type { KhoaDich } from '../../../../i18n';
import { cn } from '../../../../lib/cn';
import { KHONG_CO, soVN } from '../../../../lib/dinh-dang';
import {
  cachDienCua,
  cauLenhDaDoi,
  chenVaoTomTat,
  coThayDoi as tinhCoThayDoi,
  docJson,
  doiCachDien,
  duocLuu as tinhDuocLuu,
  inJson,
  loiTheoTab,
  oChenTomTat,
  tach,
  tachThamSoChayThu,
  type CachDien,
  type Nhap,
  type TabSoan,
} from '../../nghiep-vu/khuon-nhap';
import type { CotHienThi, KetQuaChayThu, Khuon, KieuKhuon, ThamSoKhuon } from '../../types';

/**
 * Soạn một khuôn nghiệp vụ (port `components/replica/SoanKhuon.tsx` native).
 *
 * Chia tab theo VIỆC, không xếp một cột dài: bản trước xếp mười ba ô từ trên xuống và nút Lưu nằm dưới đáy — sửa một chữ
 * trong tên là phải cuộn qua câu lệnh, hai khối JSON và phần chạy thử. Bốn tab bám bốn câu người soạn tự hỏi: trợ lý nhận
 * ra khuôn bằng gì · lấy số từ đâu · trình bày ra sao · chạy có đúng không. Thanh đầu giữ tên, trạng thái và ba nút.
 *
 * Bảng nhập thay cho JSON (gõ tay `"batBuoc": false` là mời lỗi thiếu dấu phẩy), nhưng vẫn giữ dạng JSON để dán nguyên
 * khối — và JSON đang hỏng thì KHÔNG quay về bảng được, vì bảng sẽ phải đoán phần nó không đọc ra.
 */

export const KHOI = 'rounded-lg border border-default bg-surface';

const THAM_SO_RONG: ThamSoKhuon = { ten: '', kieu: 'string', moTa: '', batBuoc: false, nguon: '', giaTri: '' };
const COT_RONG: CotHienThi = { field: '', nhan: '', kieu: 'text' };
const KIEU_THAM_SO: ThamSoKhuon['kieu'][] = ['string', 'int', 'decimal', 'date', 'bool'];
const KIEU_COT: CotHienThi['kieu'][] = ['text', 'so', 'tien', 'phan_tram', 'ngay'];
const NHAN_KIEU_COT: Record<CotHienThi['kieu'], KhoaDich> = {
  text: 'troLyAiKhuon.kieuText',
  so: 'troLyAiKhuon.kieuSo',
  tien: 'troLyAiKhuon.kieuTien',
  phan_tram: 'troLyAiKhuon.kieuPhanTram',
  ngay: 'troLyAiKhuon.kieuNgay',
};

/** Nhãn một ô, kèm dấu hỏi mở lời giải thích dài — lời giải thích nguyên văn, chỉ thôi chiếm chỗ thường trực. */
function Nhan({ chu, viSao, htmlFor }: { chu: string; viSao?: string; htmlFor?: string }) {
  const { t } = useT();
  return (
    <span className="flex items-center gap-1 text-sm text-content-muted">
      {htmlFor ? <label htmlFor={htmlFor}>{chu}</label> : chu}
      {viSao && (
        <Tooltip title={viSao} styles={{ root: { maxWidth: 420 } }}>
          <button type="button" className="cursor-help text-xs" aria-label={`${t('troLyAiKhuon.caiNay')}: ${chu}`}>
            <QuestionCircleOutlined />
          </button>
        </Tooltip>
      )}
    </span>
  );
}

export interface QuyenSoan {
  luu: boolean;
  xoa: boolean;
  chayThu: boolean;
}

export interface SoanKhuonProps {
  nhap: Nhap;
  setNhap: (n: Nhap) => void;
  nhom: { id: string; ten: string }[];
  khuon: Khuon | null;
  dialect: string;
  laApi?: boolean;
  diaChiGoc?: string;
  ketQua: KetQuaChayThu | null;
  dangLuu: boolean;
  dangChay: boolean;
  dangXoa: boolean;
  banKhaiThieu: string[];
  dangCapNhatBanKhai: boolean;
  onCapNhatBanKhai: () => void;
  dienThu: Record<string, string>;
  onDien: (v: Record<string, string>) => void;
  onLuu: () => void;
  onChayThu: () => void;
  onXoa: () => void;
  quyen: QuyenSoan;
}

export function SoanKhuon({
  nhap, setNhap, nhom, khuon, dialect, laApi = false, diaChiGoc = '', ketQua, dangLuu, dangChay, dangXoa,
  banKhaiThieu, dangCapNhatBanKhai, onCapNhatBanKhai, dienThu, onDien, onLuu, onChayThu, onXoa, quyen,
}: SoanKhuonProps) {
  const { t } = useT();
  const [tab, setTab] = useState<TabSoan>('nhan_dien');
  const [idTruoc, setIdTruoc] = useState(nhap.id);
  const doi = (p: Partial<Nhap>) => setNhap({ ...nhap, ...p });
  const thamSo = docJson<ThamSoKhuon[]>(nhap.thamSoJson, []);
  const sqlDaDoi = cauLenhDaDoi(khuon, nhap);
  const coThayDoi = tinhCoThayDoi(khuon, nhap);
  const duocLuu = tinhDuocLuu(nhap);
  const loiTab = loiTheoTab(nhap);

  // Khuôn mới mở ở tab đầu; chuyển giữa hai khuôn đã có thì GIỮ tab — người soạn đang so cùng một phần của hai khuôn.
  if (idTruoc !== nhap.id) {
    setIdTruoc(nhap.id);
    if (!nhap.id) setTab('nhan_dien');
  }

  // Ctrl/⌘+S lưu, theo đúng điều kiện của nút Lưu — không thì phím tắt lưu được lúc nút đang mờ.
  const coTheLuu = quyen.luu && duocLuu && coThayDoi && !dangLuu;
  useEffect(() => {
    const nghe = (e: KeyboardEvent) => {
      if (!(e.ctrlKey || e.metaKey) || e.key.toLowerCase() !== 's') return;
      e.preventDefault();
      if (coTheLuu) onLuu();
    };
    window.addEventListener('keydown', nghe);
    return () => window.removeEventListener('keydown', nghe);
  }, [coTheLuu, onLuu]);

  const chayThu = () => {
    setTab('chay_thu');
    onChayThu();
  };

  const nhanTab = (k: TabSoan, khoa: KhoaDich) => (
    <span className="flex items-center gap-1.5">
      {t(khoa)}
      {loiTab[k] && (
        <span className="size-1.5 rounded-full bg-danger-500" role="img" aria-label={t('troLyAiKhuon.tabCoLoi')} />
      )}
    </span>
  );

  return (
    <div className={cn(KHOI, 'flex min-w-0 flex-col')}>
      <ThanhSoan
        nhap={nhap}
        nhom={nhom}
        khuon={khuon}
        coThayDoi={coThayDoi}
        duocLuu={duocLuu}
        sqlDaDoi={sqlDaDoi}
        dangLuu={dangLuu}
        dangChay={dangChay}
        dangXoa={dangXoa}
        quyen={quyen}
        onLuu={onLuu}
        onChayThu={chayThu}
        onXoa={onXoa}
      />

      {(sqlDaDoi || banKhaiThieu.length > 0) && (
        <div className="flex flex-col gap-2 border-b border-default px-5 py-3">
          {/* Nói TRƯỚC khi lưu, không phải sau: người soạn đang sửa một khuôn đang trả lời cho người dùng thật. */}
          {sqlDaDoi && (
            <p className="flex items-start gap-2 text-xs text-warning-600">
              <WarningOutlined className="mt-0.5 shrink-0" />
              {t('troLyAiKhuon.sqlDoiThiPhaiChayLai')}
            </p>
          )}
          {banKhaiThieu.length > 0 && (
            <div className="rounded-lg border border-warning-100 bg-warning-50 px-3 py-2">
              <p className="flex flex-wrap items-center gap-1 text-xs font-medium text-warning-700">
                <WarningOutlined /> {t('troLyAiKhuon.banKhaiCu', { bang: banKhaiThieu.join(', ') })}
              </p>
              <p className="mt-1 text-xs text-warning-700">{t('troLyAiKhuon.banKhaiCuViSao')}</p>
              {quyen.luu && (
                <Button
                  className="mt-2"
                  size="sm"
                  variant="secondary"
                  icon={<ReloadOutlined />}
                  loading={dangCapNhatBanKhai}
                  onClick={onCapNhatBanKhai}
                >
                  {t('troLyAiKhuon.capNhatBanKhai')}
                </Button>
              )}
            </div>
          )}
        </div>
      )}

      {/*
        `Tabs` của antd chứ không `TabsTruot`: tab ở đây phải ĐIỀU KHIỂN được (bấm Chạy thử là nhảy sang tab Chạy thử),
        mà `TabsTruot` giữ tab đang mở trong state riêng. Lề đặt ở div bọc vì CSS của `.ant-tabs` thắng lớp Tailwind.
      */}
      <div className="px-5 pb-5 pt-1">
        <Tabs
          activeKey={tab}
          onChange={(k) => setTab(k as TabSoan)}
          items={[
            {
              key: 'nhan_dien',
              label: nhanTab('nhan_dien', 'troLyAiKhuon.tabNhanDien'),
              children: <TabNhanDien nhap={nhap} nhom={nhom} doi={doi} />,
            },
            {
              key: 'du_lieu',
              label: nhanTab('du_lieu', 'troLyAiKhuon.tabLayDuLieu'),
              children: (
                <TabLayDuLieu nhap={nhap} khuon={khuon} dialect={dialect} laApi={laApi} diaChiGoc={diaChiGoc} doi={doi} />
              ),
            },
            {
              key: 'trinh_bay',
              label: nhanTab('trinh_bay', 'troLyAiKhuon.tabTrinhBay'),
              children: <TabTrinhBay nhap={nhap} ketQua={ketQua} doi={doi} />,
            },
            {
              key: 'chay_thu',
              label: nhanTab('chay_thu', 'troLyAiKhuon.tabChayThu'),
              children: (
                <TabChayThu
                  nhap={nhap}
                  thamSo={thamSo.gia_tri}
                  khuon={khuon}
                  ketQua={ketQua}
                  sqlDaDoi={sqlDaDoi}
                  dangChay={dangChay}
                  duocChay={quyen.chayThu}
                  dienThu={dienThu}
                  onDien={onDien}
                  onChayThu={onChayThu}
                />
              ),
            },
          ]}
        />
      </div>
    </div>
  );
}

/** Thanh đầu: tên, trạng thái, ba nút. Dính ở đỉnh khi cuộn — Lưu phải bấm được từ bất cứ chỗ nào trong khuôn. */
function ThanhSoan({
  nhap, nhom, khuon, coThayDoi, duocLuu, sqlDaDoi, dangLuu, dangChay, dangXoa, quyen, onLuu, onChayThu, onXoa,
}: {
  nhap: Nhap;
  nhom: { id: string; ten: string }[];
  khuon: Khuon | null;
  coThayDoi: boolean;
  duocLuu: boolean;
  sqlDaDoi: boolean;
  dangLuu: boolean;
  dangChay: boolean;
  dangXoa: boolean;
  quyen: QuyenSoan;
  onLuu: () => void;
  onChayThu: () => void;
  onXoa: () => void;
}) {
  const { t } = useT();
  const tenNhom = nhom.find((n) => n.id === nhap.nhomId)?.ten ?? '';
  return (
    <div className="sticky top-0 z-10 flex flex-wrap items-center justify-between gap-3 rounded-t-lg border-b border-default bg-surface px-5 py-4">
      <div className="min-w-0">
        <p className="truncate text-base font-semibold text-content-primary">
          {nhap.ten.trim() || t('troLyAiKhuon.khuonMoi')}
        </p>
        <div className="mt-1 flex flex-wrap items-center gap-1.5 text-xs text-content-muted">
          {tenNhom && <span>{tenNhom}</span>}
          {tenNhom && <span aria-hidden="true">·</span>}
          <span>
            {nhap.kieu === 'api'
              ? t('troLyAiKhuon.loaiApi')
              : nhap.kieu === 'thu_tuc'
                ? t('troLyAiKhuon.loaiThuTuc')
                : t('troLyAiKhuon.loaiCauLenh')}
          </span>
          {!khuon ? (
            <Badge variant="neutral">{t('troLyAiKhuon.chuaLuu')}</Badge>
          ) : khuon.vaoBoChon ? (
            <Badge variant="success">
              <CheckCircleOutlined /> {t('troLyAiKhuon.daSanSang')}
            </Badge>
          ) : (
            <Badge variant="warning">
              <ExclamationCircleOutlined /> {t('troLyAiKhuon.chuaSanSang')}
            </Badge>
          )}
          {khuon && coThayDoi && <Badge variant="warning">{t('troLyAiKhuon.coThayDoi')}</Badge>}
        </div>
      </div>
      <div className="flex shrink-0 flex-wrap items-center gap-2">
        {khuon && quyen.xoa && (
          <Button
            variant="ghost"
            icon={<DeleteOutlined />}
            loading={dangXoa}
            title={t('troLyAiKhuon.xoa')}
            aria-label={t('troLyAiKhuon.xoa')}
            className="text-danger-600"
            onClick={onXoa}
          />
        )}
        {quyen.chayThu && (
          <Tooltip
            title={!khuon ? t('troLyAiKhuon.chayThuCanLuu') : sqlDaDoi ? t('troLyAiKhuon.luuTruocKhiChay') : undefined}
          >
            <Button
              variant="secondary"
              icon={<PlayCircleOutlined />}
              disabled={!khuon || sqlDaDoi}
              loading={dangChay}
              onClick={onChayThu}
            >
              {t('troLyAiKhuon.chayThu')}
            </Button>
          </Tooltip>
        )}
        {quyen.luu && (
          <Tooltip title={t('troLyAiKhuon.phimTatLuu')}>
            <Button
              variant="primary"
              icon={<SaveOutlined />}
              disabled={!duocLuu || (!!khuon && !coThayDoi)}
              loading={dangLuu}
              onClick={onLuu}
            >
              {t('troLyAiKhuon.luu')}
            </Button>
          </Tooltip>
        )}
      </div>
    </div>
  );
}

function TabNhanDien({
  nhap, nhom, doi,
}: {
  nhap: Nhap;
  nhom: { id: string; ten: string }[];
  doi: (p: Partial<Nhap>) => void;
}) {
  const { t } = useT();
  return (
    <div className="flex flex-col gap-4 pb-4">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">
        <div className="flex flex-col gap-1">
          <Nhan chu={t('troLyAiKhuon.ten')} htmlFor="khuon-ten" />
          <Input
            id="khuon-ten"
            value={nhap.ten}
            status={nhap.ten.trim() ? undefined : 'warning'}
            onChange={(e) => doi({ ten: e.target.value })}
            placeholder={t('troLyAiKhuon.tenPlaceholder')}
          />
        </div>
        <div className="flex flex-col gap-1">
          <Nhan chu={t('troLyAiKhuon.thuocNhom')} />
          <Select<string>
            aria-label={t('troLyAiKhuon.thuocNhom')}
            value={nhap.nhomId || undefined}
            onChange={(v) => doi({ nhomId: String(v) })}
            options={nhom.map((n) => ({ value: n.id, label: n.ten }))}
          />
        </div>
      </div>

      <div className="flex flex-col gap-1">
        <Nhan chu={t('troLyAiKhuon.cachGoi')} viSao={t('troLyAiKhuon.cachGoiViSao')} />
        {/* Ô thẻ: Enter hoặc dấu phẩy tách một cách gọi. Lưu vẫn là chuỗi phẩy — `thanLuuKhuon` tách lại. */}
        <Select<string[]>
          mode="tags"
          open={false}
          suffixIcon={null}
          tokenSeparators={[',']}
          aria-label={t('troLyAiKhuon.cachGoi')}
          value={tach(nhap.cachGoi, ',')}
          onChange={(v) => doi({ cachGoi: v.join(', ') })}
          placeholder={t('troLyAiKhuon.cachGoiPlaceholder')}
        />
      </div>

      <div className="flex flex-col gap-1">
        <Nhan chu={t('troLyAiKhuon.cauHoiMau')} viSao={t('troLyAiKhuon.cauHoiMauViSao')} htmlFor="khuon-cau-hoi" />
        <Textarea
          id="khuon-cau-hoi"
          autoSize={{ minRows: 3, maxRows: 10 }}
          value={nhap.cauHoiMau}
          onChange={(e) => doi({ cauHoiMau: e.target.value })}
          placeholder={t('troLyAiKhuon.cauHoiMauPlaceholder')}
        />
        <span className="text-xs text-content-muted">
          {t('troLyAiKhuon.soCauHoiMau', { n: tach(nhap.cauHoiMau, '\n').length })}
        </span>
      </div>
    </div>
  );
}

function TabLayDuLieu({
  nhap, khuon, dialect, laApi, diaChiGoc, doi,
}: {
  nhap: Nhap;
  khuon: Khuon | null;
  dialect: string;
  laApi: boolean;
  diaChiGoc: string;
  doi: (p: Partial<Nhap>) => void;
}) {
  const { t } = useT();
  const laThuTuc = nhap.kieu === 'thu_tuc';
  // Nguồn API: cùng khuôn với thủ tục, khác chỗ chạy — thay tên thủ tục là phương thức + đường dẫn sau địa chỉ gốc.
  if (laApi) {
    return (
      <div className="flex flex-col gap-4 pb-4">
        <div className="flex flex-col gap-1">
          <Nhan chu={t('troLyAiKhuon.duongDanApi')} viSao={t('troLyAiKhuon.duongDanApiViSao')} />
          <div className="flex flex-wrap items-center gap-2">
            <Select<'GET' | 'POST'>
              aria-label={t('troLyAiKhuon.phuongThuc')}
              className="w-28 !min-w-0"
              value={nhap.phuongThuc}
              onChange={(v) => doi({ phuongThuc: v })}
              options={[
                { value: 'GET', label: 'GET' },
                { value: 'POST', label: 'POST' },
              ]}
            />
            <span className="max-w-[18rem] truncate font-mono text-xs text-content-muted" title={diaChiGoc}>
              {diaChiGoc}
            </span>
            <Input
              aria-label={t('troLyAiKhuon.duongDanApi')}
              className="min-w-[16rem] flex-1 font-mono"
              value={nhap.duongDan}
              onChange={(e) => doi({ duongDan: e.target.value })}
              placeholder="/units/{don_vi}/reports/BCDKT"
            />
          </div>
          <span className="text-xs text-content-muted">{t('troLyAiKhuon.duongDanApiNgan')}</span>
        </div>
        <BangThamSo key={nhap.id || 'moi'} value={nhap.thamSoJson} coViTri onChange={(v) => doi({ thamSoJson: v })} />
      </div>
    );
  }
  return (
    <div className="flex flex-col gap-4 pb-4">
      <div className="flex flex-col gap-1">
        <Nhan
          chu={t('troLyAiKhuon.loaiKhuon')}
          viSao={laThuTuc ? t('troLyAiKhuon.loaiThuTucViSao') : t('troLyAiKhuon.loaiCauLenhViSao')}
        />
        <Segmented<KieuKhuon>
          block
          aria-label={t('troLyAiKhuon.loaiKhuon')}
          value={nhap.kieu}
          disabled={!!khuon}
          onChange={(v) => doi({ kieu: v })}
          options={[
            { value: 'cau_lenh', label: t('troLyAiKhuon.loaiCauLenh') },
            { value: 'thu_tuc', label: t('troLyAiKhuon.loaiThuTuc') },
          ]}
        />
        <span className="text-xs text-content-muted">
          {khuon
            ? t('troLyAiKhuon.loaiKhongDoiDuoc')
            : laThuTuc
              ? t('troLyAiKhuon.loaiThuTucNgan')
              : t('troLyAiKhuon.loaiCauLenhNgan')}
        </span>
      </div>

      {!laThuTuc ? (
        <div className="flex flex-col gap-1">
          <Nhan chu={t('troLyAiKhuon.cauLenh', { pn: dialect })} htmlFor="khuon-sql" />
          <Textarea
            id="khuon-sql"
            autoSize={{ minRows: 8, maxRows: 24 }}
            value={nhap.sql}
            onChange={(e) => doi({ sql: e.target.value })}
            className="font-mono text-xs"
            placeholder={t('troLyAiKhuon.cauLenhPlaceholder')}
          />
        </div>
      ) : (
        <>
          <div className="flex flex-col gap-1">
            <Nhan chu={t('troLyAiKhuon.tenThuTuc')} htmlFor="khuon-thu-tuc" />
            <Input
              id="khuon-thu-tuc"
              value={nhap.tenThuTuc}
              onChange={(e) => doi({ tenThuTuc: e.target.value })}
              className="font-mono"
              placeholder="USP_XAPP_DoanhThu_TheoKhachHang"
            />
          </div>
          <BangThamSo key={nhap.id || 'moi'} value={nhap.thamSoJson} onChange={(v) => doi({ thamSoJson: v })} />
        </>
      )}
    </div>
  );
}

/** Công tắc bảng ⇄ JSON dùng chung cho tham số và cột. JSON hỏng thì khoá ở dạng JSON. */
function useCheDoJson(loi: string) {
  const [json, setJson] = useState(false);
  return { laJson: json || !!loi, datJson: setJson, khoa: !!loi };
}

function CongTacJson({ laJson, khoa, datJson }: { laJson: boolean; khoa: boolean; datJson: (v: boolean) => void }) {
  const { t } = useT();
  return (
    <Tooltip title={khoa ? t('troLyAiKhuon.jsonLoiQuayBang') : undefined}>
      <Button size="sm" variant="link" disabled={khoa} onClick={() => datJson(!laJson)}>
        {laJson ? t('troLyAiKhuon.xemBang') : t('troLyAiKhuon.suaJson')}
      </Button>
    </Tooltip>
  );
}

function OTrong({ children }: { children: ReactNode }) {
  return (
    <p className="rounded-lg border border-dashed border-default px-3 py-4 text-center text-xs text-content-muted">
      {children}
    </p>
  );
}

type DongThamSo = ThamSoKhuon & { _i: number };

function BangThamSo({
  value, coViTri = false, onChange,
}: {
  value: string;
  /** Khuôn API: thêm cột "đi vào đâu" — đường dẫn, chuỗi truy vấn hay thân JSON. */
  coViTri?: boolean;
  onChange: (v: string) => void;
}) {
  const { t } = useT();
  const { gia_tri: dong, loi } = docJson<ThamSoKhuon[]>(value, []);
  const { laJson, datJson, khoa } = useCheDoJson(loi);
  // Chọn "Giá trị cố định" mà chưa gõ giá trị thì JSON không có gì để nhớ lựa chọn ấy — giữ ở đây theo vị trí dòng.
  const [coDinh, setCoDinh] = useState<Set<number>>(new Set());

  const ghi = (ds: ThamSoKhuon[]) => onChange(inJson(ds));
  const sua = (i: number, p: Partial<ThamSoKhuon>) => ghi(dong.map((x, j) => (j === i ? { ...x, ...p } : x)));
  const bo = (i: number) => {
    setCoDinh(new Set([...coDinh].filter((j) => j !== i).map((j) => (j > i ? j - 1 : j))));
    ghi(dong.filter((_, j) => j !== i));
  };
  const doiCach = (i: number, c: CachDien) => {
    const ds = new Set(coDinh);
    if (c === 'co_dinh') ds.add(i);
    else ds.delete(i);
    setCoDinh(ds);
    const x = dong[i];
    if (x) ghi(dong.map((y, j) => (j === i ? doiCachDien(x, c) : y)));
  };

  const cot: TableProps<DongThamSo>['columns'] = [
    {
      title: t('troLyAiKhuon.cotTen'),
      key: 'ten',
      width: 160,
      render: (_, x) => (
        <Input
          size="small"
          className="font-mono"
          aria-label={t('troLyAiKhuon.cotTen')}
          value={x.ten}
          placeholder="p_thang"
          onChange={(e) => sua(x._i, { ten: e.target.value })}
        />
      ),
    },
    {
      title: t('troLyAiKhuon.cotKieu'),
      key: 'kieu',
      width: 112,
      render: (_, x) => (
        <Select<ThamSoKhuon['kieu']>
          size="small"
          className="w-full !min-w-0"
          aria-label={t('troLyAiKhuon.cotKieu')}
          value={x.kieu}
          onChange={(v) => sua(x._i, { kieu: v })}
          options={KIEU_THAM_SO.map((k) => ({ value: k, label: k }))}
        />
      ),
    },
    ...(coViTri
      ? [
          {
            title: t('troLyAiKhuon.cotViTri'),
            key: 'viTri',
            width: 128,
            render: (_: unknown, x: DongThamSo) => (
              <Select<NonNullable<ThamSoKhuon['viTri']>>
                size="small"
                className="w-full !min-w-0"
                aria-label={t('troLyAiKhuon.cotViTri')}
                value={x.viTri ?? 'query'}
                onChange={(v) => sua(x._i, { viTri: v })}
                options={[
                  { value: 'query', label: t('troLyAiKhuon.viTriQuery') },
                  { value: 'path', label: t('troLyAiKhuon.viTriPath') },
                  { value: 'body', label: t('troLyAiKhuon.viTriBody') },
                ]}
              />
            ),
          },
        ]
      : []),
    {
      title: t('troLyAiKhuon.cotCachDien'),
      key: 'cachDien',
      width: 208,
      render: (_, x) => (
        <Select<CachDien>
          size="small"
          className="w-full !min-w-0"
          aria-label={t('troLyAiKhuon.cotCachDien')}
          value={cachDienCua(x, coDinh.has(x._i))}
          onChange={(v) => doiCach(x._i, v)}
          options={[
            { value: 'tro_li', label: t('troLyAiKhuon.dienTroLi') },
            { value: 'co_dinh', label: t('troLyAiKhuon.dienCoDinh') },
            { value: 'dang_nhap', label: t('troLyAiKhuon.dienDangNhap') },
          ]}
        />
      ),
    },
    {
      title: t('troLyAiKhuon.cotMoTaGiaTri'),
      key: 'moTa',
      render: (_, x) => (
        <OMoTaGiaTri x={x} c={cachDienCua(x, coDinh.has(x._i))} onSua={(p) => sua(x._i, p)} />
      ),
    },
    {
      title: t('troLyAiKhuon.cotBatBuoc'),
      key: 'batBuoc',
      width: 80,
      align: 'center',
      render: (_, x) => (
        <Checkbox
          aria-label={t('troLyAiKhuon.cotBatBuoc')}
          checked={x.batBuoc}
          disabled={cachDienCua(x, coDinh.has(x._i)) !== 'tro_li'}
          onChange={(e) => sua(x._i, { batBuoc: e.target.checked })}
        />
      ),
    },
    {
      title: <span className="sr-only">{t('troLyAiKhuon.boDong')}</span>,
      key: 'bo',
      width: 48,
      render: (_, x) => (
        <Button
          size="sm"
          variant="ghost"
          icon={<DeleteOutlined />}
          title={t('troLyAiKhuon.boDong')}
          aria-label={t('troLyAiKhuon.boDong')}
          onClick={() => bo(x._i)}
        />
      ),
    },
  ];

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center justify-between">
        <Nhan chu={t('troLyAiKhuon.thamSo')} viSao={t('troLyAiKhuon.thamSoViSao')} />
        <CongTacJson laJson={laJson} khoa={khoa} datJson={datJson} />
      </div>
      {laJson ? (
        <OJson nhan={t('troLyAiKhuon.thamSo')} anNhan goiY={t('troLyAiKhuon.thamSoJsonGoiY')} value={value} rows={10} onChange={onChange} />
      ) : (
        <>
          {dong.length === 0 ? (
            <OTrong>{t('troLyAiKhuon.chuaCoThamSo')}</OTrong>
          ) : (
            <div className="rounded-lg border border-default">
              <DataTable<DongThamSo>
                caption={t('troLyAiKhuon.bangThamSo')}
                size="small"
                rowKey="_i"
                pagination={false}
                columns={cot}
                dataSource={dong.map((x, i) => ({ ...x, _i: i }))}
              />
            </div>
          )}
          <Button
            size="sm"
            variant="secondary"
            icon={<PlusOutlined />}
            className="self-start"
            onClick={() => ghi([...dong, THAM_SO_RONG])}
          >
            {t('troLyAiKhuon.themThamSo')}
          </Button>
        </>
      )}
    </div>
  );
}

/**
 * Ô thứ tư của một dòng tham số đổi nghĩa theo cách điền: trợ lý điền thì cần MÔ TẢ (thứ duy nhất cho trợ lý biết tháng
 * là 1-12), cố định thì cần GIÁ TRỊ, nền tảng điền thì không cần gì.
 */
function OMoTaGiaTri({ x, c, onSua }: { x: ThamSoKhuon; c: CachDien; onSua: (p: Partial<ThamSoKhuon>) => void }) {
  const { t } = useT();
  if (c === 'dang_nhap') return <span className="text-xs text-content-muted">{t('troLyAiKhuon.vaiNguoiHoi')}</span>;
  if (c === 'co_dinh') {
    return (
      <Input
        size="small"
        className="font-mono"
        aria-label={t('troLyAiKhuon.dienCoDinh')}
        value={x.giaTri}
        placeholder="NUOC_SACH"
        onChange={(e) => onSua({ giaTri: e.target.value })}
      />
    );
  }
  return (
    <div className="flex min-w-[12rem] flex-col gap-0.5">
      <Input
        size="small"
        aria-label={t('troLyAiKhuon.cotMoTaGiaTri')}
        value={x.moTa}
        status={x.moTa.trim() ? undefined : 'warning'}
        placeholder={t('troLyAiKhuon.moTaThamSoPlaceholder')}
        onChange={(e) => onSua({ moTa: e.target.value })}
      />
      {!x.moTa.trim() && <span className="text-2xs text-warning-600">{t('troLyAiKhuon.thieuMoTa')}</span>}
    </div>
  );
}

type DongCot = CotHienThi & { _i: number };

function BangCot({ value, cotKetQua, onChange }: { value: string; cotKetQua: string[]; onChange: (v: string) => void }) {
  const { t } = useT();
  const { gia_tri: dong, loi } = docJson<CotHienThi[]>(value, []);
  const { laJson, datJson, khoa } = useCheDoJson(loi);
  const ghi = (ds: CotHienThi[]) => onChange(inJson(ds));
  const sua = (i: number, p: Partial<CotHienThi>) => ghi(dong.map((x, j) => (j === i ? { ...x, ...p } : x)));
  const daCo = new Set(dong.map((x) => x.field));
  const conThieu = cotKetQua.filter((c) => !daCo.has(c));

  const cot: TableProps<DongCot>['columns'] = [
    {
      title: t('troLyAiKhuon.cotField'),
      key: 'field',
      width: 224,
      render: (_, x) => (
        <AutoComplete
          size="small"
          className="w-full font-mono"
          aria-label={t('troLyAiKhuon.cotField')}
          value={x.field}
          options={cotKetQua.map((c) => ({ value: c }))}
          onChange={(v: string) => sua(x._i, { field: v })}
          placeholder="doanh_thu_vnd"
        />
      ),
    },
    {
      title: t('troLyAiKhuon.cotNhan'),
      key: 'nhan',
      render: (_, x) => (
        <Input
          size="small"
          aria-label={t('troLyAiKhuon.cotNhan')}
          value={x.nhan}
          placeholder={t('troLyAiKhuon.cotNhanPlaceholder')}
          onChange={(e) => sua(x._i, { nhan: e.target.value })}
        />
      ),
    },
    {
      title: t('troLyAiKhuon.cotKieu'),
      key: 'kieu',
      width: 128,
      render: (_, x) => (
        <Select<CotHienThi['kieu']>
          size="small"
          className="w-full !min-w-0"
          aria-label={t('troLyAiKhuon.cotKieu')}
          value={x.kieu}
          onChange={(v) => sua(x._i, { kieu: v })}
          options={KIEU_COT.map((k) => ({ value: k, label: t(NHAN_KIEU_COT[k]) }))}
        />
      ),
    },
    {
      title: <span className="sr-only">{t('troLyAiKhuon.boDong')}</span>,
      key: 'bo',
      width: 48,
      render: (_, x) => (
        <Button
          size="sm"
          variant="ghost"
          icon={<DeleteOutlined />}
          title={t('troLyAiKhuon.boDong')}
          aria-label={t('troLyAiKhuon.boDong')}
          onClick={() => ghi(dong.filter((_, j) => j !== x._i))}
        />
      ),
    },
  ];

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center justify-between">
        <Nhan chu={t('troLyAiKhuon.cotHienThi')} viSao={t('troLyAiKhuon.cotHienThiViSao')} />
        <CongTacJson laJson={laJson} khoa={khoa} datJson={datJson} />
      </div>
      {laJson ? (
        <OJson nhan={t('troLyAiKhuon.cotHienThi')} anNhan goiY={t('troLyAiKhuon.cotJsonGoiY')} value={value} rows={8} onChange={onChange} />
      ) : (
        <>
          {dong.length === 0 ? (
            <OTrong>{t('troLyAiKhuon.chuaCoCot')}</OTrong>
          ) : (
            <div className="rounded-lg border border-default">
              <DataTable<DongCot>
                caption={t('troLyAiKhuon.bangCot')}
                size="small"
                rowKey="_i"
                pagination={false}
                columns={cot}
                dataSource={dong.map((x, i) => ({ ...x, _i: i }))}
              />
            </div>
          )}
          <div className="flex flex-wrap gap-2">
            <Button size="sm" variant="secondary" icon={<PlusOutlined />} onClick={() => ghi([...dong, COT_RONG])}>
              {t('troLyAiKhuon.themCot')}
            </Button>
            {/* Cột lấy từ lần chạy thử là tên THẬT thủ tục trả về — gõ tay sai một chữ là cột lặng lẽ hiện tên gốc. */}
            {conThieu.length > 0 && (
              <Button
                size="sm"
                variant="secondary"
                onClick={() => ghi([...dong, ...conThieu.map((c) => ({ ...COT_RONG, field: c }))])}
              >
                {t('troLyAiKhuon.layCotTuKetQua', { n: conThieu.length })}
              </Button>
            )}
          </div>
        </>
      )}
    </div>
  );
}

function TabTrinhBay({ nhap, ketQua, doi }: { nhap: Nhap; ketQua: KetQuaChayThu | null; doi: (p: Partial<Nhap>) => void }) {
  const { t } = useT();
  const oChen = oChenTomTat(nhap);
  const coTongHop = nhap.kieu !== 'cau_lenh' || !['', '{}'].includes(nhap.tongHopJson.trim());

  return (
    <div className="flex flex-col gap-4 pb-4">
      <div className="flex flex-col gap-1">
        <Nhan chu={t('troLyAiKhuon.tieuDe')} htmlFor="khuon-tieu-de" />
        <Input
          id="khuon-tieu-de"
          value={nhap.tieuDe}
          onChange={(e) => doi({ tieuDe: e.target.value })}
          placeholder={t('troLyAiKhuon.tieuDePlaceholder')}
        />
      </div>

      <div className="flex flex-col gap-1">
        <Nhan chu={t('troLyAiKhuon.tomTat')} viSao={t('troLyAiKhuon.tomTatViSao')} htmlFor="khuon-tom-tat" />
        <Textarea
          id="khuon-tom-tat"
          autoSize={{ minRows: 2, maxRows: 6 }}
          value={nhap.tomTat}
          onChange={(e) => doi({ tomTat: e.target.value })}
          placeholder={t('troLyAiKhuon.tomTatPlaceholder')}
        />
        <div className="flex flex-wrap items-center gap-1">
          <span className="text-xs text-content-muted">{t('troLyAiKhuon.chen')}</span>
          {oChen.map((o) => (
            <button
              key={o}
              type="button"
              aria-label={t('troLyAiKhuon.chenO', { o: `{${o}}` })}
              className="rounded border border-default px-1.5 py-0.5 font-mono text-2xs hover:bg-subtle"
              onClick={() => doi({ tomTat: chenVaoTomTat(nhap.tomTat, o) })}
            >
              {`{${o}}`}
            </button>
          ))}
        </div>
      </div>

      <BangCot
        key={nhap.id || 'moi'}
        value={nhap.cotJson}
        cotKetQua={ketQua?.columns ?? []}
        onChange={(v) => doi({ cotJson: v })}
      />

      {coTongHop && (
        <details className="rounded-lg border border-default px-3 py-2">
          <summary className="cursor-pointer text-sm text-content-muted">{t('troLyAiKhuon.nangCao')}</summary>
          <div className="pt-2">
            <OJson
              nhan={t('troLyAiKhuon.tongHop')}
              goiY={t('troLyAiKhuon.tongHopViSao')}
              value={nhap.tongHopJson}
              rows={4}
              onChange={(v) => doi({ tongHopJson: v })}
            />
          </div>
        </details>
      )}
    </div>
  );
}

function TabChayThu({
  nhap, thamSo, khuon, ketQua, sqlDaDoi, dangChay, duocChay, dienThu, onDien, onChayThu,
}: {
  nhap: Nhap;
  thamSo: ThamSoKhuon[];
  khuon: Khuon | null;
  ketQua: KetQuaChayThu | null;
  sqlDaDoi: boolean;
  dangChay: boolean;
  duocChay: boolean;
  dienThu: Record<string, string>;
  onDien: (v: Record<string, string>) => void;
  onChayThu: () => void;
}) {
  const { t } = useT();
  const { tuDien, moHoi } = tachThamSoChayThu(thamSo);

  return (
    <div className="grid grid-cols-1 gap-4 pb-4 xl:grid-cols-[minmax(0,1fr)_minmax(0,18rem)]">
      <div className="flex min-w-0 flex-col gap-3">
        {!khuon ? (
          <p className="rounded-lg border border-dashed border-default px-3 py-6 text-center text-sm text-content-muted">
            {t('troLyAiKhuon.chayThuCanLuu')}
          </p>
        ) : (
          <>
            {nhap.kieu !== 'cau_lenh' && thamSo.length > 0 && (
              <div className="rounded-lg border border-default p-3">
                <Nhan chu={t('troLyAiKhuon.thamSoChayThu')} viSao={t('troLyAiKhuon.thamSoChayThuViSao')} />
                {/*
                  Tham số NỀN TẢNG điền không hiện thành ô nhập: bản trước vẽ ô cho `p_vai`, người soạn gõ vào rồi máy chủ
                  ghi đè mất — một ô gõ được nhưng bị vứt đi là chỗ người dùng tin rằng mình đã khai một thứ chưa khai.
                */}
                {tuDien.length > 0 && (
                  <p className="mt-1 text-xs text-content-muted">
                    {t('troLyAiKhuon.nenTangTuDien')}:{' '}
                    {tuDien.map((ts) => `${ts.ten} = ${ts.giaTri || t('troLyAiKhuon.vaiNguoiHoi')}`).join(' · ')}
                  </p>
                )}
                <div className="mt-2 grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
                  {moHoi.map((ts) => (
                    <label key={ts.ten} className="flex min-w-0 flex-col gap-1 text-xs">
                      <span className="truncate text-content-muted" title={ts.moTa}>
                        <span className="font-mono">{ts.ten}</span>
                        {ts.moTa ? ` — ${ts.moTa}` : ''}
                      </span>
                      <Input
                        size="small"
                        value={dienThu[ts.ten] ?? ''}
                        onChange={(e) => onDien({ ...dienThu, [ts.ten]: e.target.value })}
                      />
                    </label>
                  ))}
                </div>
              </div>
            )}
            {duocChay && (
              <Button
                variant="secondary"
                icon={<PlayCircleOutlined />}
                className="self-start"
                disabled={sqlDaDoi}
                loading={dangChay}
                onClick={onChayThu}
              >
                {sqlDaDoi ? t('troLyAiKhuon.luuTruocKhiChay') : t('troLyAiKhuon.chayThu')}
              </Button>
            )}
            {ketQua ? (
              <BangKetQuaChayThu kq={ketQua} />
            ) : (
              <p className="text-xs text-content-muted">{t('troLyAiKhuon.chuaChayThuPhien')}</p>
            )}
          </>
        )}
      </div>
      <PhamVi khuon={khuon} />
    </div>
  );
}

/**
 * Ô nhập JSON, kèm báo lỗi cú pháp TẠI CHỖ — một mảng thiếu dấu phẩy mà chỉ nhận lại "lưu không được" là phải dò bằng mắt.
 */
function OJson({
  nhan, anNhan = false, goiY, value, rows, onChange,
}: {
  nhan: string;
  /** Nhãn đã hiện ở ngay trên (đầu bảng) — chỉ giữ cho trình đọc màn hình. */
  anNhan?: boolean;
  goiY: string;
  value: string;
  rows: number;
  onChange: (v: string) => void;
}) {
  const { loi } = docJson<unknown>(value, null);
  return (
    <label className="flex flex-col gap-1 text-sm">
      <span className={anNhan ? 'sr-only' : 'text-content-muted'}>{nhan}</span>
      <Textarea
        rows={rows}
        value={value}
        status={loi ? 'error' : undefined}
        onChange={(e) => onChange(e.target.value)}
        className="font-mono text-xs"
      />
      {loi ? (
        <span role="alert" className="text-xs text-danger-700">
          {loi}
        </span>
      ) : (
        <span className="text-xs text-content-muted">{goiY}</span>
      )}
    </label>
  );
}

/**
 * Phạm vi — đúng những bảng và cột mô hình sẽ được thấy. `congDon` là dòng đáng đọc nhất: lời khai "cột này cộng lại có
 * nghĩa" mà khuôn vừa đưa ra thay cho người vận hành.
 */
function PhamVi({ khuon }: { khuon: Khuon | null }) {
  const { t } = useT();
  if (!khuon) {
    return (
      <div className="rounded-lg border border-default p-3">
        <p className="text-sm text-content-muted">{t('troLyAiKhuon.phamViChuaLuu')}</p>
      </div>
    );
  }
  const { bang, cot, congDon } = khuon.phamVi;
  return (
    <div className="flex flex-col gap-3 rounded-lg border border-default p-3">
      {!khuon.daChayThu && <p className="text-xs text-warning-600">{t('troLyAiKhuon.viSaoChuaSanSang')}</p>}
      {khuon.unverifiedReason && <p className="text-xs text-danger-600">{khuon.unverifiedReason}</p>}

      <div>
        <p className="text-sm font-medium text-content-primary">{t('troLyAiKhuon.phamViTieuDe')}</p>
        <p className="text-xs text-content-muted">{t('troLyAiKhuon.phamViViSao')}</p>
      </div>

      {bang.length === 0 ? (
        <p className="text-sm text-content-muted">{t('troLyAiKhuon.phamViRong')}</p>
      ) : (
        <ul className="flex flex-col gap-2">
          {bang.map((b) => (
            <li key={b} className="rounded-lg border border-default px-2 py-1.5">
              <p className="font-mono text-xs font-medium">{b}</p>
              <p className="mt-0.5 break-words font-mono text-2xs text-content-muted">
                {(cot[b] ?? []).join(', ') || t('troLyAiKhuon.moiCot')}
              </p>
              {(congDon[b] ?? []).length > 0 && (
                <p className="mt-1 break-words text-2xs text-success-700">
                  {t('troLyAiKhuon.congDonLa', { cot: (congDon[b] ?? []).join(', ') })}
                </p>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function BangKetQuaChayThu({ kq }: { kq: KetQuaChayThu }) {
  const { t, locale } = useT();
  const dong = kq.rows.slice(0, 10).map((r, i) => ({ ...r, __dong: i }));
  const cot: TableProps<Record<string, unknown>>['columns'] = kq.columns.map((c) => ({
    title: c,
    key: c,
    render: (_: unknown, r: Record<string, unknown>) => (
      <span className="whitespace-nowrap font-mono text-xs">
        {r[c] === null || r[c] === undefined ? KHONG_CO : String(r[c])}
      </span>
    ),
  }));
  return (
    <div className="flex flex-col gap-2">
      <p className="text-xs text-content-muted">
        {t('troLyAiKhuon.ketQuaTomTat', { n: soVN(kq.rowCount, locale), ms: soVN(kq.elapsedMs, locale) })}
        {kq.soTap && kq.soTap > 1 ? ` · ${t('troLyAiKhuon.soTap', { n: soVN(kq.soTap, locale) })}` : ''}
      </p>
      {/* Lời gọi ĐÃ CHẠY, kèm tham số: chỗ người soạn thấy ngay một giá trị bị điền sai kỳ. */}
      {kq.sql.startsWith('EXEC') && <p className="break-all rounded bg-subtle px-2 py-1 font-mono text-xs">{kq.sql}</p>}
      {kq.tomTat ? <p className="rounded-lg border border-default px-2 py-1.5 text-sm">{kq.tomTat}</p> : null}
      {kq.tongHop ? (
        <p className="text-xs text-content-muted">
          {t('troLyAiKhuon.dongTongHop')}:{' '}
          {Object.entries(kq.tongHop)
            .map(([k, v]) => `${k}=${String(v)}`)
            .join(' · ')}
        </p>
      ) : null}
      {kq.rowCount === 0 ? (
        // 0 dòng KHÔNG phải lỗi: câu lệnh đúng, kho chỉ chưa có dữ liệu cho kỳ ấy. Nhưng nói ra, để người soạn tự cân.
        <p className="text-xs text-warning-600">{t('troLyAiKhuon.khongDongViSao')}</p>
      ) : (
        <DataTable<Record<string, unknown>>
          caption={t('troLyAiKhuon.bangKetQua')}
          size="small"
          rowKey="__dong"
          pagination={false}
          columns={cot}
          dataSource={dong}
        />
      )}
    </div>
  );
}
